/**
 * Reports Page
 * Phase 6 Batch 3 - Dashboard & Reporting System
 *
 * Comprehensive reporting dashboard with all 8 reports:
 * Core 4:
 * - Dashboard Stats (key metrics)
 * - Revenue Trend (6-month chart)
 * - Projects by Stage (workflow distribution)
 * - Outstanding Invoices (collections tracking)
 *
 * Advanced 4:
 * - Revenue Pipeline (forward-looking cash flow)
 * - Cash Flow Forecast (90-day outlook)
 * - Project Profitability (profitability analysis)
 * - Ball in Court (action tracker)
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { DashboardStatsCards } from '@/components/reports/DashboardStatsCards';
import { RevenueChart } from '@/components/reports/RevenueChart';
import { ProjectsStageChart } from '@/components/reports/ProjectsStageChart';
import { OutstandingInvoicesTable } from '@/components/reports/OutstandingInvoicesTable';
import { RevenuePipelineChart } from '@/components/reports/RevenuePipelineChart';
import { CashFlowForecastChart } from '@/components/reports/CashFlowForecastChart';
import { ProjectProfitabilityTable } from '@/components/reports/ProjectProfitabilityTable';
import { BallInCourtTable } from '@/components/reports/BallInCourtTable';
import { DrilldownsTab } from '@/components/reports/DrilldownsTab';
import { BarChart3 } from 'lucide-react';

type ReportTab =
  | 'dashboard-stats'
  | 'revenue-by-month'
  | 'projects-by-stage'
  | 'outstanding-invoices'
  | 'revenue-pipeline'
  | 'cash-flow-forecast'
  | 'project-profitability'
  | 'ball-in-court'
  | 'drilldowns';

export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ReportTab | null;
  const [activeTab, setActiveTab] = useState<ReportTab>(
    tabParam && ['dashboard-stats', 'revenue-by-month', 'projects-by-stage', 'outstanding-invoices', 'revenue-pipeline', 'cash-flow-forecast', 'project-profitability', 'ball-in-court', 'drilldowns'].includes(tabParam)
      ? tabParam
      : 'dashboard-stats'
  );

  // Sync URL params when tab changes
  const handleTabChange = (tab: ReportTab) => {
    setActiveTab(tab);
    if (tab === 'drilldowns') {
      setSearchParams({ tab: 'drilldowns', view: 'available-to-invoice' });
    } else {
      setSearchParams({ tab });
    }
  };

  const tabs: Array<{ id: ReportTab; label: string; category: 'core' | 'eos' | 'drilldowns' }> = [
    { id: 'dashboard-stats', label: 'Dashboard Stats', category: 'core' },
    { id: 'revenue-by-month', label: 'Revenue Trend', category: 'core' },
    { id: 'projects-by-stage', label: 'Projects by Stage', category: 'core' },
    { id: 'outstanding-invoices', label: 'Outstanding Invoices', category: 'core' },
    { id: 'revenue-pipeline', label: 'Revenue Pipeline', category: 'eos' },
    { id: 'cash-flow-forecast', label: 'Cash Flow Forecast', category: 'eos' },
    { id: 'project-profitability', label: 'Project Profitability', category: 'eos' },
    { id: 'ball-in-court', label: 'Ball in Court', category: 'eos' },
    { id: 'drilldowns', label: 'Drilldowns', category: 'drilldowns' },
  ];

  // Lazy load: only render the active tab's component
  const renderActiveReport = () => {
    switch (activeTab) {
      case 'dashboard-stats':
        return <DashboardStatsCards />;
      case 'revenue-by-month':
        return <RevenueChart months={6} />;
      case 'projects-by-stage':
        return <ProjectsStageChart />;
      case 'outstanding-invoices':
        return <OutstandingInvoicesTable />;
      case 'revenue-pipeline':
        return <RevenuePipelineChart months={6} />;
      case 'cash-flow-forecast':
        return <CashFlowForecastChart days={90} />;
      case 'project-profitability':
        return <ProjectProfitabilityTable />;
      case 'ball-in-court':
        return <BallInCourtTable />;
      case 'drilldowns':
        return <DrilldownsTab />;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 size={28} />
              Reports & Analytics
            </h1>
            <p className="text-gray-600 mt-1">
              Business metrics, revenue trends, and collections tracking
            </p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            {/* Core 4 Tabs */}
            <div className="px-6 pt-4 pb-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Core Reports
              </h3>
              <div className="flex flex-wrap gap-2">
                {tabs
                  .filter((tab) => tab.category === 'core')
                  .map((tab) => (
                    <button
                      key={tab.id}
                      data-testid={`tab-${tab.id}`}
                      onClick={() => handleTabChange(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-50 text-sky-700 border-b-2 border-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
              </div>
            </div>

            {/* Advanced Tabs */}
            <div className="px-6 py-2 border-t border-gray-100">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Advanced Reports
              </h3>
              <div className="flex flex-wrap gap-2 pb-2">
                {tabs
                  .filter((tab) => tab.category === 'eos')
                  .map((tab) => (
                    <button
                      key={tab.id}
                      data-testid={`tab-${tab.id}`}
                      onClick={() => handleTabChange(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
                        activeTab === tab.id
                          ? 'bg-purple-50 text-violet-700 border-b-2 border-purple-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
              </div>
            </div>

            {/* Drilldowns Tab */}
            <div className="px-6 py-2 border-t border-gray-100">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Drilldowns
              </h3>
              <div className="flex flex-wrap gap-2 pb-2">
                {tabs
                  .filter((tab) => tab.category === 'drilldowns')
                  .map((tab) => (
                    <button
                      key={tab.id}
                      data-testid={`tab-${tab.id}`}
                      onClick={() => handleTabChange(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
                        activeTab === tab.id
                          ? 'bg-green-50 text-green-700 border-b-2 border-green-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* Active Report Content */}
          <div className="p-6" data-testid={`${activeTab}-content`}>
            {renderActiveReport()}
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
